import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useData } from '../context/DataContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShare2, FiCopy, FiCheck, FiX, FiExternalLink, FiZap, FiEye } = FiIcons;

const ShareModal = ({ website, onClose }) => {
  const { shareProject } = useData();
  const [selectedPages, setSelectedPages] = useState([]);
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  const togglePageSelection = (pageId) => {
    setSelectedPages(prev =>
      prev.includes(pageId)
        ? prev.filter(id => id !== pageId)
        : [...prev, pageId]
    );
  };

  const handleShare = async () => {
    if (selectedPages.length === 0) {
      alert('Please select at least one page to share');
      return;
    }

    setLoading(true);
    try {
      const shareId = await shareProject(website.id, selectedPages);
      const url = `${window.location.origin}/#/client/${shareId}`;
      setShareUrl(url);
    } catch (error) {
      console.error('Error sharing project:', error);
      alert('Error creating share link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="bg-white/90 backdrop-blur-xl rounded-2xl lg:rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden border border-white/20"
        >
          {/* Enhanced Header */}
          <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 p-4 sm:p-6 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-24 h-24 sm:w-32 sm:h-32 bg-white/10 rounded-full -mr-12 -mt-12 sm:-mr-16 sm:-mt-16" />
            <div className="flex items-center justify-between relative z-10">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiShare2} className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold">Share Project</h2>
                  <p className="text-blue-100 text-sm sm:text-base truncate">{website.name}</p>
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors flex-shrink-0"
              >
                <SafeIcon icon={FiX} className="w-4 h-4 sm:w-5 sm:h-5" />
              </motion.button>
            </div>
          </div>

          <div className="p-4 sm:p-6 max-h-[calc(90vh-200px)] overflow-y-auto">
            {!shareUrl ? (
              <>
                <div className="mb-4 sm:mb-6">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                    Select pages to share with your client
                  </h3>
                  <p className="text-gray-600 text-sm sm:text-base">
                    Choose which pages and their SEO data you want to include in the client report.
                  </p>
                </div>

                <div className="space-y-2 sm:space-y-3 mb-6 sm:mb-8 max-h-64 sm:max-h-80 overflow-y-auto">
                  {website.pages?.map((page) => (
                    <motion.label
                      key={page.id}
                      whileHover={{ scale: 1.02 }}
                      className="flex items-start space-x-3 sm:space-x-4 p-3 sm:p-4 bg-gray-50/80 backdrop-blur-sm hover:bg-gray-100/80 rounded-xl lg:rounded-2xl cursor-pointer transition-all duration-200 border border-gray-200"
                    >
                      <input
                        type="checkbox"
                        checked={selectedPages.includes(page.id)}
                        onChange={() => togglePageSelection(page.id)}
                        className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 mt-1 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-2">
                          <h4 className="font-medium text-gray-900 text-sm sm:text-base truncate">{page.title}</h4>
                          <div className="flex items-center space-x-2 mt-1 sm:mt-0">
                            {page.ranking && (
                              <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-semibold flex-shrink-0">
                                {page.ranking}
                              </span>
                            )}
                            {page.social_media_created && (
                              <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full font-semibold flex items-center space-x-1 flex-shrink-0">
                                <SafeIcon icon={FiShare2} className="w-2 h-2 sm:w-3 sm:h-3" />
                                <span>Social</span>
                              </span>
                            )}
                          </div>
                        </div>
                        <p className="text-xs sm:text-sm text-gray-600 mb-2 line-clamp-2">{page.content}</p>
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-1 sm:space-y-0 sm:space-x-4">
                          <span className="text-xs text-gray-500">
                            {page.keyPhrases?.length || 0} key phrases
                          </span>
                          <motion.a
                            whileHover={{ scale: 1.05 }}
                            href={page.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:text-blue-800 flex items-center space-x-1 self-start sm:self-auto"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <SafeIcon icon={FiExternalLink} className="w-3 h-3" />
                            <span>View</span>
                          </motion.a>
                        </div>
                      </div>
                    </motion.label>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleShare}
                    disabled={loading || selectedPages.length === 0}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg flex items-center justify-center space-x-2 text-sm sm:text-base"
                  >
                    {loading ? (
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full"
                      />
                    ) : (
                      <SafeIcon icon={FiZap} className="w-4 h-4 sm:w-5 sm:h-5" />
                    )}
                    <span>{loading ? 'Generating Link...' : 'Generate Share Link'}</span>
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={onClose}
                    className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors text-sm sm:text-base"
                  >
                    Cancel
                  </motion.button>
                </div>
              </>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center"
              >
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-green-500 to-green-600 rounded-xl lg:rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <SafeIcon icon={FiCheck} className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Share Link Created!</h3>
                <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">
                  Your client can access this professional SEO report using the link below.
                </p>

                <div className="bg-gray-50/80 backdrop-blur-sm rounded-xl lg:rounded-2xl p-3 sm:p-4 mb-4 sm:mb-6 border border-gray-200">
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                    <div className="flex-1 bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 text-left">
                      <p className="text-xs sm:text-sm text-gray-600 mb-1">Share URL:</p>
                      <p className="text-blue-600 font-mono text-xs sm:text-sm break-all">{shareUrl}</p>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={copyToClipboard}
                      className="w-full sm:w-auto p-2 sm:p-3 bg-blue-600 text-white rounded-lg sm:rounded-xl hover:bg-blue-700 transition-colors shadow-lg flex items-center justify-center space-x-2 text-sm sm:text-base"
                    >
                      <SafeIcon icon={copied ? FiCheck : FiCopy} className="w-4 h-4 sm:w-5 sm:h-5" />
                      <span className="sm:hidden">{copied ? 'Copied!' : 'Copy'}</span>
                    </motion.button>
                  </div>
                </div>

                <AnimatePresence>
                  {copied && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="bg-green-50 border border-green-200 text-green-800 px-3 sm:px-4 py-2 rounded-xl text-xs sm:text-sm mb-3 sm:mb-4"
                    >
                      ✓ Link copied to clipboard!
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => window.open(shareUrl, '_blank')}
                    className="flex-1 bg-gradient-to-r from-green-600 to-green-700 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold hover:from-green-700 hover:to-green-800 transition-all duration-200 shadow-lg flex items-center justify-center space-x-2 text-sm sm:text-base"
                  >
                    <SafeIcon icon={FiEye} className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span>Preview Report</span>
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={onClose}
                    className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors text-sm sm:text-base"
                  >
                    Done
                  </motion.button>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ShareModal;